import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { APIError } from '@data';
import { teamInvitesKeys, trialActivityKeys, userTrialsKeys, usersKeys } from '@data/constants';
import {
  AcceptExistingUserInviteBody,
  AcceptNewUserInviteBody,
  InviteApiAcceptMilestoneTokenExistingRequest,
  InviteApiAcceptMilestoneTokenRequest,
  InviteBody,
  InviteView,
  LoginView,
  TeamCreateInviteBody,
  TeamCreateInviteView,
  TeamMemberInviteBody,
  TrialInviteUpdateBody,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';

export const useAcceptInvite = (): UseMutationResult<
  LoginView,
  APIError<AcceptNewUserInviteBody>,
  AcceptNewUserInviteBody,
  unknown
> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.invites.acceptInviteUnauthorized({
        acceptNewUserInviteBody: values,
      });
      return response.data;
    },
  });

export const useAcceptInviteAsExistingUser = (): UseMutationResult<
  LoginView,
  APIError,
  AcceptExistingUserInviteBody,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.invites.acceptInviteAuthorized({
        acceptExistingUserInviteBody: values,
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trialActivityKeys.all() }),
  });
};

export const useCreateInviteToTeam = (): UseMutationResult<
  InviteView,
  APIError,
  { teamId: string; values: TeamMemberInviteBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, values }) => {
      const response = await MatchingApi.invites.createTeamInvite({
        teamId,
        teamMemberInviteBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { teamId }) =>
      queryClient.invalidateQueries({ queryKey: teamInvitesKeys.list(teamId) }),
  });
};

export const useDeleteTeamInvite = (): UseMutationResult<
  unknown,
  APIError,
  { teamId: string; tokenValue: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, tokenValue }) => {
      const response = await MatchingApi.invites.deleteTeamInvite({ teamId, tokenValue });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamInvitesKeys.all() }),
  });
};

export const useUpdateTeamMemberInvite = (): UseMutationResult<
  InviteView,
  APIError,
  { teamId: string; values: TrialInviteUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, values }) => {
      const response = await MatchingApi.invites.updateTeamMemberInvite({
        teamId,
        trialInviteUpdateBody: values,
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamInvitesKeys.all() }),
  });
};

// trial bulk invite
export const useCreateTeamInvite = (): UseMutationResult<
  TeamCreateInviteView,
  APIError,
  TeamCreateInviteBody,
  unknown
> => {
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.invites.createTeamCreationInvite({
        teamCreateInviteBody: values,
      });

      return response.data;
    },
  });
};

// accept trial invite with existing team
export const useAcceptTrialInviteWithExistingTeam = (): UseMutationResult<
  void,
  APIError,
  { teamId: string; token: string },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ teamId, token }) => {
      await MatchingApi.invites.acceptTrialInviteWithExistingTeam({
        acceptExistingTeamInviteBody: {
          tokenValue: token,
        },
        teamId,
      });
    },
  });
};

export const useCreateInvite = (): UseMutationResult<void, APIError, InviteBody, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteBody) => {
      const response = await MatchingApi.invites.createInvite({
        inviteBody,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.admins() });
    },
  });
};

export const useAcceptMilestoneInvite = (): UseMutationResult<
  LoginView,
  APIError,
  InviteApiAcceptMilestoneTokenRequest,
  unknown
> => {
  return useMutation({
    mutationFn: async (body) => {
      const response = await MatchingApi.invites.acceptMilestoneToken(body);
      return response.data;
    },
  });
};

export const useAcceptMilestoneInviteAsExistingUser = (): UseMutationResult<
  void,
  APIError,
  InviteApiAcceptMilestoneTokenExistingRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const response = await MatchingApi.invites.acceptMilestoneTokenExisting(body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userTrialsKeys.all() });
      queryClient.invalidateQueries({ queryKey: trialActivityKeys.all() });
    },
  });
};
